/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * Turnstile — minimal Cloudflare Turnstile widget wrapper.
 *
 * - Loads https://challenges.cloudflare.com/turnstile/v0/api.js once per page.
 * - Renders the widget in a container div; calls onVerify(token) when solved.
 * - Exposes a reset() function via ref so the login page can re-arm after a
 *   server-side rejection.
 * - Site key from REACT_APP_TURNSTILE_SITE_KEY at build time (Vercel env).
 *
 * Zero dependencies. Traditional <script> load + window.turnstile.render().
 */

import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Load the Turnstile script once. Returns a promise that resolves when
// window.turnstile is ready.
let _scriptPromise = null;
function ensureTurnstileScript() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src^="${SCRIPT_SRC.split('?')[0]}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = (e) => {
      // Forget the failed load so a Retry can try again. Without this the
      // rejected promise stays cached and every Retry fails instantly.
      _scriptPromise = null;
      try { s.remove(); } catch (_) {}
      reject(e);
    };
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

const Turnstile = forwardRef(function Turnstile(
  { onVerify, onExpire, onError, onRendered, theme = 'auto', size = 'normal', action },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useImperativeHandle(ref, () => ({
    reset() {
      if (window.turnstile && widgetIdRef.current) {
        try { window.turnstile.reset(widgetIdRef.current); } catch (_) {}
      }
    },
    getResponse() {
      if (window.turnstile && widgetIdRef.current) {
        try { return window.turnstile.getResponse(widgetIdRef.current); } catch (_) {}
      }
      return null;
    }
  }), []);

  useEffect(() => {
    let cancelled = false;
    if (!siteKey) {
      console.warn('[Turnstile] REACT_APP_TURNSTILE_SITE_KEY not set — widget disabled');
      return () => {};
    }

    ensureTurnstileScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme, size, action,
          callback: (token) => { if (onVerify) onVerify(token); },
          'expired-callback': () => { if (onExpire) onExpire(); },
          'error-callback': (err) => { if (onError) onError(err); },
          // Interactive challenge left unanswered too long, or browser not
          // supported: both are real failures the user must see.
          'timeout-callback': () => { if (onError) onError(new Error('turnstile_timeout')); },
          'unsupported-callback': () => { if (onError) onError(new Error('turnstile_unsupported')); }
        });
        // Script loaded and widget is on the page. From here Cloudflare
        // reports success or failure itself, however long the check takes.
        if (onRendered) onRendered();
      } catch (e) {
        console.error('[Turnstile] render failed:', e && e.message);
        if (onError) onError(e);
      }
    }).catch((e) => {
      console.error('[Turnstile] script load failed:', e && e.message);
      if (onError) onError(e);
    });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) {
    // Non-fatal in dev: allow the form to submit without the widget, so
    // developers without keys aren't blocked. Server-side kill switch
    // (CAPTCHA_ENABLED=false or missing secret in non-prod) accepts empty.
    return null;
  }

  return <div ref={containerRef} className="cf-turnstile" style={{ margin: '12px 0' }} />;
});

export default Turnstile;
